import type { Doc } from '../_generated/dataModel'
import type { QueryCtx } from '../_generated/server'
import type { PlanTerm } from './planTerm'

export { type PlanTerm, planTermValidator } from './planTerm'
export {
  PAYWALL_FLAG,
  type PaywallVariant,
  asPaywallVariant,
  paywallVariantValidator,
} from './paywallVariant'

export const DAY_MS = 86_400_000
export const PRO_TRIAL_DAYS = 7
// Covers late renewal webhooks.
const PAID_GRACE_MS = 3 * DAY_MS

export type PlanTier = 'free' | 'trial' | 'pro'

// Fallback only: shown prices come from Polar (web) and the stores (native).
export const PLAN_PRICES_USD: Record<PlanTerm, number> = {
  month: 12.99,
  year: 59.99,
  lifetime: 149,
}

// Founder lifetime is a one-off payment against ongoing AI costs, so it is
// seat-capped and gets a share of Pro's metered AI caps.
export const LIFETIME_SEATS = 500
export const LIFETIME_AI_SHARE = 0.5

// Effective plan = paid Pro, an unexpired comped-Pro window (referral trial /
// reward), or an unexpired no-card Pro trial. Paid/comped always wins over the
// trial; everything lapses at read time so expiry needs no cleanup.
export function withEffectivePlan(
  sub: Doc<'userSubscriptions'> | null,
  trialEndsAt?: number,
  now = Date.now(),
  noCardTrial = true,
) {
  // A paid period that ran out counts as lapsed even if the cancel webhook never
  // arrived; renewals push expiresAt forward. Lifetime and manual grants have none.
  const lapsed = sub?.expiresAt != null && sub.expiresAt + PAID_GRACE_MS < now
  const paid =
    !!sub && ((sub.plan === 'pro' && !lapsed) || (sub.proUntil != null && sub.proUntil > now))
  const trialActive = !paid && trialEndsAt != null && trialEndsAt > now
  const tier: PlanTier = paid ? 'pro' : trialActive ? 'trial' : 'free'
  return {
    ...sub,
    plan: tier === 'free' ? ('free' as const) : ('pro' as const),
    tier,
    trial: {
      active: trialActive,
      endsAt: trialEndsAt ?? null,
      daysLeft: trialActive ? Math.ceil((trialEndsAt - now) / DAY_MS) : 0,
    },
    trialEligible: noCardTrial && !paid && trialEndsAt == null,
    // A store/Polar trial (card on file) is offered once, to someone who never had either trial.
    cardTrialEligible: !paid && trialEndsAt == null && sub?.trialStartedAt == null,
    lifetime: !!sub && sub.plan === 'pro' && sub.term === 'lifetime',
  }
}

export async function getPlanForUser(ctx: QueryCtx, userId: string, now = Date.now()) {
  const [sub, prefs] = await Promise.all([
    ctx.db
      .query('userSubscriptions')
      .withIndex('by_user', (q) => q.eq('userId', userId))
      .first(),
    ctx.db
      .query('userPreferences')
      .withIndex('by_user', (q) => q.eq('userId', userId))
      .first(),
  ])
  return withEffectivePlan(
    sub,
    prefs?.proTrialEndsAt,
    now,
    prefs?.paywallVariant !== 'trial-paywall',
  )
}
